'use client';

// 분양광고 배너의 브라우저 쪽 동작 — 화면에는 아무것도 그리지 않는다(숨은 표식 1개).
//  ① 로테이터: 같은 HTML 의 소재(최대 3)를 4~6초 간격으로 번갈아 켠다. 진입마다 순서는 랜덤(서버 HTML 은 항상 첫 번째).
//     순서 = 유료끼리만 무작위(결제·시작 순서 아님) → 그 뒤 테스트·하우스 소재(서버 순서) · CSS order 로도 같은 순서(DOM 이동 없음).
//     쿠폰 배너는 로테이터 밖(뒤)이라 늘 마지막.
//     넘김은 배너가 50% 이상 «1초 넘게» 보일 때만 · 탭이 숨으면 멈춤 · «동작 줄이기» 설정이면 자동 넘김 없음 · 멈춤 버튼은 두지 않는다(Node 결정 10/27 — 화면에 광고 조작 버튼을 보이지 않음).
//  ② 노출 계측: 켜진 소재가 뷰포트 50% 이상 1초 → ad_view(소재·경로당 30분 1회 — sessionStorage kd_adv:{versionId}:{pagePath}). 클릭 → ad_click. 기존 user_events 경로(lib/analytics track).
//  ③ 이미지 실패 → 단색 배경 + 문구(data-broken) · 깨진 소재는 ad_view 를 보내지 않는다.
//  봇 UA·자동화 브라우저는 기록하지 않는다.

import { useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { classifyBot } from '@/lib/bot-classify';
import { canRotate, nextDelay, rotationOrder, ROTATE_MIN_VISIBLE_MS, ROTATE_MIN_VISIBLE_RATIO } from '@/lib/ads/rotator-core';

/** 로테이터 소재에 이번 방문 순서를 매긴다(유료끼리 섞기 · 비유료는 뒤) — CSS order 만(DOM 이동 없음). 보여 줄 순서(소재 번호)를 돌려준다. 테스트용 export. */
export function applyRotatorOrder(items: readonly HTMLElement[], rand: () => number = Math.random): number[] {
  const seq = rotationOrder(items.map((x) => x.dataset.adPaid === '1'), rand);
  seq.forEach((idx, pos) => { items[idx].style.order = String(pos); });
  return seq;
}

/**
 * L3 — 노출 중복 제거: 같은 탭(sessionStorage)에서 같은 소재·같은 경로는 30분에 1회만 ad_view.
 *   종전엔 페이지 수명(in-memory)만이라 새로고침·뒤로가기마다 다시 셌다. sessionStorage 를 못 쓰는 환경(차단·사생활 모드)은 메모리로만.
 */
export const AD_VIEW_SEEN_TTL_MS = 30 * 60_000;
export const adViewSeenKey = (versionId: string, pagePath: string): string => `kd_adv:${versionId}:${pagePath}`;
const SEEN = new Set<string>();

/** 30분 안에 이미 본 키인가(기록하지 않음). */
export function hasSeen(key: string, now: number = Date.now()): boolean {
  if (SEEN.has(key)) return true;
  try {
    const raw = sessionStorage.getItem(key);
    const at = raw ? Number(raw) : NaN;
    if (Number.isFinite(at) && now - at < AD_VIEW_SEEN_TTL_MS) { SEEN.add(key); return true; }
  } catch { /* 저장소 없음 — 메모리만 */ }
  return false;
}

/** 같은 노출 키를 30분에 한 번만 — 처음이면 true(기록하고), 이미 봤으면 false. */
export function markSeenOnce(key: string, now: number = Date.now()): boolean {
  if (hasSeen(key, now)) return false;
  SEEN.add(key);
  try { sessionStorage.setItem(key, String(now)); } catch { /* 저장소 없음 — 메모리만 */ }
  return true;
}

/** 소재 안 이미지가 깨졌는가(data-broken) — 깨진 소재는 노출로 세지 않는다. */
export function isBrokenCreative(el: HTMLElement | null): boolean {
  if (!el) return false;
  return el.hasAttribute('data-broken') || !!el.querySelector('[data-broken]');
}

function botLike(): boolean {
  try {
    if ((navigator as any).webdriver) return true;
    // 계측 제외만(화면은 모두 같다 — 클로킹 아님). 분류는 page_views 와 같은 lib/bot-classify 하나로.
    return classifyBot(navigator.userAgent) !== 'human';
  } catch { return true; }
}

export interface AdEventProps { campaignId: string; versionId: string; placement: string; pagePath: string; sido?: string; sigungu?: string; surface?: string; /** 연결 방식 — 「전화 문의」(tel:) 클릭이면 'call'(합계는 같은 ad_click · 안에서만 구분) */ action?: 'call' }

/** 클릭한 링크가 「전화 문의」(tel:)면 { action: 'call' } — 홈페이지 클릭은 종전 그대로(속성 없음). */
export function actionOf(a: Element | null): { action?: 'call' } {
  return (a as HTMLElement | null)?.dataset?.adAction === 'call' ? { action: 'call' } : {};
}

/** 레인 R — 덤 노출 자리(data-ad-surface: search·search_suggest) → 이벤트 속성 surface. 기존 페이지 배너는 싣지 않는다(성과표에서 «페이지 배너»). */
export function surfaceOf(el: HTMLElement | null): { surface?: string } {
  const root = el?.closest?.<HTMLElement>('[data-paid-ad-root]') ?? null;
  const v = el?.dataset?.adSurface || root?.dataset?.adSurface || '';
  return /^(search|search_suggest)$/.test(v) ? { surface: v } : {};
}

/** lane-A — 시군구 배너 루트의 data-ad-sido / data-ad-sigungu(페이지의 시·도/시군구) → 이벤트 속성. 없으면 싣지 않는다. */
export function areaAttrsOf(el: HTMLElement | null): { sido?: string; sigungu?: string } {
  const root = el?.closest?.<HTMLElement>('[data-paid-ad-root]') ?? null;
  const sido = el?.dataset?.adSido || root?.dataset?.adSido || '';
  const sigungu = el?.dataset?.adSigungu || root?.dataset?.adSigungu || '';
  return { ...(sido ? { sido } : {}), ...(sigungu ? { sigungu } : {}) };
}

function propsOf(el: HTMLElement | null): AdEventProps | null {
  if (!el) return null;
  const d = el.dataset;
  if (!d.adCampaign || !d.adVersion) return null;
  return { campaignId: d.adCampaign, versionId: d.adVersion, placement: d.adPlacement ?? '', pagePath: typeof window !== 'undefined' ? window.location.pathname : '', ...areaAttrsOf(el), ...surfaceOf(el) };
}

export function sendAdEvent(name: 'ad_view' | 'ad_click', props: AdEventProps) {
  if (botLike()) return;
  track(name, name, props);
}

export default function PaidAdClient({ rotate = false }: { rotate?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const root = ref.current?.closest<HTMLElement>('[data-paid-ad-root]');
    if (!root) return;
    const items = root.hasAttribute('data-ad-campaign') ? [root] : Array.from(root.querySelectorAll<HTMLElement>('[data-ad-campaign]'));
    if (items.length === 0) return;
    const cleanups: Array<() => void> = [];

    // ③ 이미지 실패 대체
    root.querySelectorAll<HTMLImageElement>('.pad__media img').forEach((img) => {
      const mark = () => img.parentElement?.setAttribute('data-broken', '1');
      if (img.complete && img.naturalWidth === 0) mark();
      img.addEventListener('error', mark, { once: true });
      cleanups.push(() => img.removeEventListener('error', mark));
    });

    let active = Math.max(0, items.findIndex((x) => x.dataset.on === '1'));
    let inView = false;
    let visibleSince: number | null = null;
    let viewTimer: ReturnType<typeof setTimeout> | null = null;
    const pagePath = window.location.pathname;
    const reducedMotion = (() => {
      try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
    })();

    const armView = () => {
      if (viewTimer) { clearTimeout(viewTimer); viewTimer = null; }
      if (!inView || document.hidden) return;
      const p = propsOf(items[active]);
      if (!p) return;
      const key = adViewSeenKey(p.versionId, pagePath);
      if (hasSeen(key)) return;
      viewTimer = setTimeout(() => {
        if (!inView || document.hidden) return;
        if (isBrokenCreative(items[active])) return; // 이미지 실패 소재는 노출로 세지 않는다
        if (markSeenOnce(key)) sendAdEvent('ad_view', p);
      }, ROTATE_MIN_VISIBLE_MS);
    };

    const setActive = (i: number) => {
      active = i;
      items.forEach((x, n) => { if (n === i) x.dataset.on = '1'; else delete x.dataset.on; });
      armView();
    };

    // ① 로테이터 — 멈춤 버튼은 없다(Node 결정). «동작 줄이기» 설정이면 넘기지 않는다.
    let rotTimer: ReturnType<typeof setTimeout> | null = null;
    let seq: number[] = items.map((_, i) => i);
    let pos = 0;
    const schedule = () => {
      if (rotTimer) { clearTimeout(rotTimer); rotTimer = null; }
      rotTimer = setTimeout(() => {
        if (canRotate({ visibleSince, hidden: document.hidden, paused: false, reducedMotion }, Date.now())) { pos = (pos + 1) % seq.length; setActive(seq[pos]); }
        schedule();
      }, nextDelay());
    };
    if (rotate && items.length > 1) {
      seq = applyRotatorOrder(items); // 방문마다 유료끼리 무작위 순서(비유료는 뒤)
      pos = 0;
      setActive(seq[0]);
      if (!reducedMotion) schedule();
      const onVis = () => { if (document.hidden) { if (viewTimer) clearTimeout(viewTimer); } else armView(); };
      document.addEventListener('visibilitychange', onVis);
      cleanups.push(() => {
        if (rotTimer) clearTimeout(rotTimer);
        document.removeEventListener('visibilitychange', onVis);
      });
    }

    // ② 노출 — 50% 이상 보이기 시작한 시각을 기억(넘김 조건과 노출 계측이 같은 기준)
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((es) => {
        const vis = es.some((e) => e.isIntersecting && e.intersectionRatio >= ROTATE_MIN_VISIBLE_RATIO);
        if (vis && !inView) visibleSince = Date.now();
        if (!vis) visibleSince = null;
        inView = vis;
        armView();
      }, { threshold: [0, ROTATE_MIN_VISIBLE_RATIO, 1] });
      io.observe(root);
      cleanups.push(() => io.disconnect());
    }

    // ② 클릭
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[data-paid-ad-link]');
      if (!a || !root.contains(a)) return;
      const p = propsOf(a.closest<HTMLElement>('[data-ad-campaign]'));
      if (p) sendAdEvent('ad_click', { ...p, ...actionOf(a) });
    };
    root.addEventListener('click', onClick);
    cleanups.push(() => root.removeEventListener('click', onClick));

    return () => {
      if (viewTimer) clearTimeout(viewTimer);
      cleanups.forEach((f) => f());
    };
  }, [rotate]);

  return <span ref={ref} hidden aria-hidden="true" data-paid-ad-probe="" />;
}
